import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { getToken, setToken, removeToken } from '../utils/cookies';
import { API_BASE_URL, IMAGE_BASE_URLS } from '../utils/imageUrl';

// Timeout for queued requests waiting for token refresh (30 seconds)
const TOKEN_REFRESH_QUEUE_TIMEOUT_MS = 30000;

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

interface QueueItem {
  resolve: (token: string) => void;
  reject: (error: Error) => void;
}

let isRefreshing = false;
let failedQueue: QueueItem[] = [];

const processQueue = (error: Error | null, token: string | null = null): void => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else if (token) {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Request interceptor to add JWT token to headers
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

// Response interceptor for handling token refresh
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // If 401 and not already retrying, attempt token refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Don't refresh if the failed request was the refresh endpoint itself
      if (originalRequest.url === 'auth/refresh') {
        removeToken();
        // Dispatch event to notify App.tsx to redirect to login
        window.dispatchEvent(new CustomEvent('auth:logout'));
        return Promise.reject(error);
      }

      if (isRefreshing) {
        // Queue requests while refresh is in progress with timeout
        return new Promise<string>((resolve, reject) => {
          const timeoutId = setTimeout(() => {
            // Remove this item from the queue if still present
            const index = failedQueue.findIndex((item) => item.resolve === resolve);
            if (index !== -1) {
              failedQueue.splice(index, 1);
            }
            reject(new Error('Token refresh timeout - please log in again'));
          }, TOKEN_REFRESH_QUEUE_TIMEOUT_MS);

          failedQueue.push({
            resolve: (token: string) => {
              clearTimeout(timeoutId);
              resolve(token);
            },
            reject: (err: Error) => {
              clearTimeout(timeoutId);
              reject(err);
            },
          });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return api(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const currentToken = getToken();
        const response = await api.post<{ access_token: string }>('auth/refresh', {
          access_token: currentToken,
        });
        const { access_token } = response.data;

        setToken(access_token);
        processQueue(null, access_token);

        originalRequest.headers.Authorization = `Bearer ${access_token}`;
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError as Error, null);
        removeToken();
        // Dispatch event to notify App.tsx to redirect to login
        window.dispatchEvent(new CustomEvent('auth:logout'));
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

// Re-export image URL utilities for backward compatibility
export { IMAGE_BASE_URLS };
export const IMAGE_BASE_URL = IMAGE_BASE_URLS.mmc;

export default api;
